import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiErrorText } from '../lib/apiError';
import useSWR from 'swr';
import { ExternalLink, HelpCircle, Check, Cloud, Copy, Loader2, Trash2, KeyRound, Send, AlertTriangle, Plus } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import TabPageLayout from './TabPageLayout';
import { BeaconIcon } from './BeaconIcon';
import apiV2, { orgApi } from '../utils/apiV2';
import GoogleAdsSetupGuide from './GoogleAdsSetupGuide';
import { QuickBooksIntegrationCard } from './QuickBooksIntegrationCard';

const fetcher = async () => {
  const response = await orgApi.getOrganization();
  return response.data;
};

// Soft, borderless card surface that matches the Branding/Settings aesthetic.
const Section = ({ icon, title, description, children }) => (
  <div className="card-soft p-5 sm:p-6">
    <div className="flex items-start gap-3 mb-5">
      <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div>
        <h3 className="settings-section-title">{title}</h3>
        {description && <p className="settings-section-subtitle">{description}</p>}
      </div>
    </div>
    {children}
  </div>
);

const GoogleAdsIcon = (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
    <path d="M12.87 15.07l-2.54-2.51.03-.03A17.52 17.52 0 0014.07 6H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z" fill="#4285F4" />
  </svg>
);

const GA4Icon = (
  <svg className="w-5 h-5" viewBox="0 0 192 192" fill="none">
    <path d="M96 16C64 16 32 48 32 80c0 24 16 44 40 52-4-8-6-16-6-24 0-20 14-38 30-50 0 20 10 36 24 46 4-10 6-20 6-32 8 12 14 26 14 42 0 12-4 24-10 34 24-8 40-28 40-52 0-48-40-80-74-80z" fill="#F57C00" />
    <path d="M96 96c-12 8-20 22-20 38 0 20 16 36 36 36s36-16 36-36c0-28-24-50-52-38z" fill="#FF9800" />
  </svg>
);

const BfIcon = <BeaconIcon className="w-5 h-5 text-[#2563EB]" />;

const GTMIcon = (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
    <path d="M12 1.5 22.5 12 12 22.5l-3.4-3.5 4.9-5-6-6L12 1.5z" fill="#8AB4F8" />
    <path d="M12 1.5 1.5 12l7.1 7-3.4 3.5L12 22.5l3.4-3.5L5.6 9.2 12 1.5z" fill="#4285F4" />
    <circle cx="12" cy="19.6" r="2.6" fill="#246FDB" />
  </svg>
);

const BfBeaconSection = ({ orgId }) => {
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [copied, setCopied] = useState(false);

  const check = useCallback(async () => {
    if (!orgId) return;
    setChecking(true);
    try {
      const r = await apiV2.get(`/organizations/${orgId}/intelligence/beacon-status`);
      setStatus(r.data);
    } catch { /* non-blocking */ } finally {
      setChecking(false);
    }
  }, [orgId]);
  useEffect(() => { check(); }, [check]);

  const copy = () => {
    navigator.clipboard.writeText(orgId || '').then(() => {
      setCopied(true);
      toast.success('Beacon ID copied');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Section
      icon={BfIcon}
      title="BookingFlows Beacon"
      description="Our own website analytics — pageviews, referrers, and how visits turn into bookings"
    >
      <div className="space-y-4">
        <div>
          <Label htmlFor="bfBeaconId" className="field-label">Beacon ID</Label>
          <div className="relative mt-1">
            <Input
              id="bfBeaconId"
              value={orgId || ''}
              readOnly
              className="font-mono pr-9 text-gray-600 bg-gray-50/60"
              data-testid="bf-beacon-id-input"
            />
            <button
              onClick={copy}
              data-testid="bf-beacon-copy"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex items-center justify-center transition-colors"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2.5" data-testid="bf-beacon-status">
          {status?.firingRecently ? (
            <>
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
              </span>
              <span className="text-xs text-gray-500">Live · {status.totalPageviews?.toLocaleString()} pageviews</span>
            </>
          ) : status?.installed ? (
            <>
              <span className="inline-flex rounded-full h-1.5 w-1.5 bg-gray-300" />
              <span className="text-xs text-gray-500">Installed · quiet for 48h</span>
            </>
          ) : (
            <>
              <span className="inline-flex rounded-full h-1.5 w-1.5 ring-1 ring-gray-300 bg-transparent" />
              <span className="text-xs text-gray-400">{checking ? 'Checking…' : 'Not installed yet'}</span>
            </>
          )}
          <button
            onClick={check}
            disabled={checking}
            data-testid="bf-beacon-recheck"
            className="text-[11px] text-gray-400 hover:text-gray-600 transition-colors"
          >
            re-check
          </button>
        </div>
      </div>

      <a
        href="/intelligence/website-analytics"
        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 mt-4"
        data-testid="bf-beacon-helm-link"
      >
        Install &amp; view analytics in HELM Intelligence
        <ExternalLink className="w-3.5 h-3.5" />
      </a>
      <a
        href="https://chromewebstore.google.com/detail/bookingflows-beacon-tende/lomkgfganpkdkeeeipjdngkcmhbbahkh"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="bf-beacon-tender-store-link"
        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-2 w-fit"
      >
        <ExternalLink className="w-3 h-3" />
        Verify it&apos;s firing with BF Beacon Tender (Chrome extension)
      </a>
    </Section>
  );
};

const MetaIcon = (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877F2" />
  </svg>
);

const ZapierIcon = (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#FF4F00" aria-hidden="true">
    <path d="M14.13 12.01c0 .62-.11 1.22-.32 1.77-.55.21-1.15.33-1.78.33h-.01c-.63 0-1.23-.12-1.78-.33-.21-.55-.33-1.15-.33-1.77v-.01c0-.63.12-1.23.33-1.78.55-.21 1.15-.33 1.78-.33h.01c.63 0 1.23.12 1.78.33.21.55.32 1.15.32 1.78v.01zm7.6-1.35h-5.71l4.04-4.04a10.6 10.6 0 0 0-1.07-1.26 10.6 10.6 0 0 0-1.26-1.07l-4.04 4.04V2.62c-.54-.09-1.1-.14-1.66-.14h-.01c-.57 0-1.12.05-1.67.14v5.71L6.31 4.29c-.45.32-.87.68-1.26 1.07-.39.39-.75.81-1.07 1.26l4.04 4.04H2.31s-.14 1.1-.14 1.67v.01c0 .57.05 1.12.14 1.66h5.71l-4.04 4.04c.64.9 1.43 1.69 2.33 2.33l4.04-4.04v5.71c.54.09 1.1.14 1.66.14h.02c.56 0 1.12-.05 1.66-.14v-5.71l4.04 4.04c.45-.32.87-.68 1.26-1.07.39-.39.75-.81 1.07-1.26l-4.04-4.04h5.71c.09-.54.14-1.1.14-1.66v-.02c0-.56-.05-1.12-.14-1.66z" />
  </svg>
);

const EVENT_LABELS = {
  'booking.confirmed': 'Booking confirmed',
  'lead.created': 'Lead created',
};

const ZapierSection = () => {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [newKey, setNewKey] = useState(null); // plaintext — shown once
  const [keyBusy, setKeyBusy] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);
  const [hookUrl, setHookUrl] = useState('');
  const [hookEvent, setHookEvent] = useState('booking.confirmed');
  const [addingHook, setAddingHook] = useState(false);
  const [testing, setTesting] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await apiV2.get('/integrations/zapier/status');
      setStatus(r.data);
      return r.data;
    } catch { /* non-blocking */ } finally {
      setLoadingStatus(false);
    }
    return null;
  }, []);
  useEffect(() => { load(); }, [load]);

  // Less friction: pre-generate the key the first time the card is opened —
  // it's sitting there ready to copy. Rotation stays available afterwards.
  const autoKeyTried = useRef(false);
  useEffect(() => {
    if (loadingStatus || autoKeyTried.current) return;
    if (status && !status.connected) {
      autoKeyTried.current = true;
      (async () => {
        setKeyBusy(true);
        try {
          const r = await apiV2.post('/integrations/zapier/generate-key');
          setNewKey(r.data.apiKey);
          await load();
        } catch { /* card still shows manual button on failure */ } finally {
          setKeyBusy(false);
        }
      })();
    }
  }, [status, loadingStatus, load]);

  const [showRotateConfirm, setShowRotateConfirm] = useState(false);

  const doGenerateKey = async () => {
    setShowRotateConfirm(false);
    setKeyBusy(true);
    try {
      const r = await apiV2.post('/integrations/zapier/generate-key');
      setNewKey(r.data.apiKey);
      setKeyCopied(false);
      await load();
      toast.success('Zapier API key ready — copy it now');
    } catch {
      toast.error('Could not generate the key');
    } finally {
      setKeyBusy(false);
    }
  };

  const generateKey = () => {
    if (status?.connected) {
      setShowRotateConfirm(true);
      return;
    }
    doGenerateKey();
  };

  const copyKey = () => {
    navigator.clipboard.writeText(newKey || '').then(() => {
      setKeyCopied(true);
      toast.success('API key copied');
      setTimeout(() => setKeyCopied(false), 2000);
    });
  };

  const addHook = async () => {
    const url = hookUrl.trim();
    if (!url) return;
    setAddingHook(true);
    try {
      await apiV2.post('/integrations/zapier/webhooks', { url, event: hookEvent });
      setHookUrl('');
      await load();
      toast.success('Webhook saved — it will fire on the next event');
    } catch (e) {
      toast.error(apiErrorText(e, 'Could not save the webhook'));
    } finally {
      setAddingHook(false);
    }
  };

  const removeHook = async (id) => {
    setRemovingId(id);
    try {
      await apiV2.delete(`/integrations/zapier/webhooks/${id}`);
      await load();
      toast.success('Webhook removed');
    } catch {
      toast.error('Could not remove the webhook');
    } finally {
      setRemovingId(null);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await apiV2.post('/integrations/zapier/test', { event: hookEvent });
      const ok = (r.data.results || []).filter((x) => x.ok).length;
      const total = (r.data.results || []).length;
      if (ok === total) toast.success(`Test delivered to ${ok}/${total} webhook${total === 1 ? '' : 's'} — check your Zap history`);
      else toast.warning(`Delivered to ${ok}/${total} webhooks — check the failing URL`);
      await load();
    } catch (e) {
      toast.error(apiErrorText(e, 'Test failed'));
    } finally {
      setTesting(false);
    }
  };

  const hooks = status?.hooks || [];
  const events = status?.events || [];
  const hasHooksForEvent = hooks.some((h) => h.active && h.event === hookEvent);

  return (
    <Section
      icon={ZapierIcon}
      title="Zapier"
      description="Send booking events to 8,000+ apps — Mailchimp, Slack, Google Sheets, Xero and more"
    >
      {loadingStatus ? (
        <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-5">
          {/* API key (for the BookingFlows app on Zapier) */}
          <div>
            <Label className="field-label">Zapier API key</Label>
            <p className="text-xs text-gray-400 mt-0.5 mb-2">
              Paste this into Zapier when connecting your BookingFlows account.
            </p>
            {newKey ? (
              <div className="space-y-2">
                <div className="relative">
                  <Input
                    value={newKey}
                    readOnly
                    onFocus={(e) => e.target.select()}
                    className="font-mono pr-9 text-gray-700 bg-amber-50/60 border-amber-200"
                    data-testid="zapier-new-key-input"
                  />
                  <button
                    onClick={copyKey}
                    data-testid="zapier-key-copy"
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex items-center justify-center transition-colors"
                  >
                    {keyCopied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
                <p className="text-[11px] text-amber-600 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  Copy this key now — it won&apos;t be shown again.
                </p>
              </div>
            ) : status?.connected ? (
              <div className="flex items-center gap-2 flex-wrap">
                <code className="text-xs font-mono text-gray-500 bg-gray-50 rounded-md px-2.5 py-1.5 ring-1 ring-gray-100" data-testid="zapier-key-hint">
                  {status.keyHint}
                </code>
                <button
                  onClick={generateKey}
                  disabled={keyBusy}
                  data-testid="zapier-rotate-key"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {keyBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                  Rotate key
                </button>
              </div>
            ) : keyBusy ? (
              <div className="flex items-center gap-2 text-sm text-gray-400 py-1.5" data-testid="zapier-key-preparing">
                <Loader2 className="w-4 h-4 animate-spin" /> Preparing your key…
              </div>
            ) : (
              <button
                onClick={generateKey}
                disabled={keyBusy}
                data-testid="zapier-generate-key"
                className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60 transition-colors shadow-sm"
              >
                <KeyRound className="w-4 h-4" />
                Generate key
              </button>
            )}
          </div>

          {/* Direct Catch Hook — works today, no Zapier app needed */}
          <div>
            <Label className="field-label">Webhook URLs (Catch Hooks)</Label>
            <p className="text-xs text-gray-400 mt-0.5 mb-2">
              In Zapier, create a Zap that <span className="font-medium text-gray-600">catches a webhook</span> — trigger <span className="font-medium text-gray-600">“Webhooks by Zapier → Catch Hook”</span>. Zapier generates a webhook URL; paste it here and we send your events to it.
            </p>
            <div className="flex gap-2 flex-wrap sm:flex-nowrap items-stretch">
              <select
                value={hookEvent}
                onChange={(e) => setHookEvent(e.target.value)}
                data-testid="zapier-hook-event-select"
                className="h-10 rounded-md border border-input bg-white px-2.5 text-sm text-gray-700 shrink-0"
              >
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>{ev.label}</option>
                ))}
              </select>
              <Input
                value={hookUrl}
                onChange={(e) => setHookUrl(e.target.value)}
                placeholder="https://hooks.zapier.com/hooks/catch/…"
                className="font-mono text-xs flex-1 min-w-[180px] h-10"
                data-testid="zapier-hook-url-input"
              />
              <button
                onClick={addHook}
                disabled={addingHook || !hookUrl.trim()}
                data-testid="zapier-add-hook"
                className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40 transition-colors shrink-0 shadow-sm"
              >
                {addingHook ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Add
              </button>
            </div>

            {hooks.length > 0 && (
              <div className="mt-3 space-y-1.5" data-testid="zapier-hooks-list">
                {hooks.map((h) => (
                  <div key={h.id} className="flex items-center gap-2.5 rounded-lg bg-gray-50/70 px-3 py-2" data-testid={`zapier-hook-row-${h.id}`}>
                    <span
                      className={`inline-flex rounded-full h-1.5 w-1.5 shrink-0 ${
                        !h.active ? 'bg-gray-300'
                          : h.lastStatus && h.lastStatus >= 200 && h.lastStatus < 300 ? 'bg-emerald-500'
                          : h.lastStatus ? 'bg-red-400' : 'bg-gray-300'
                      }`}
                      title={!h.active ? 'Inactive' : h.lastStatus ? `Last delivery: HTTP ${h.lastStatus}` : 'No deliveries yet'}
                    />
                    <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400 shrink-0 w-24 sm:w-28">
                      {EVENT_LABELS[h.event] || h.event}
                    </span>
                    <span className="text-xs font-mono text-gray-500 truncate flex-1" title={h.url}>{h.url}</span>
                    {h.source === 'rest_hook' && (
                      <span className="text-[10px] text-gray-400 bg-white ring-1 ring-gray-200 rounded px-1.5 py-0.5 shrink-0">Zap</span>
                    )}
                    <button
                      onClick={() => removeHook(h.id)}
                      disabled={removingId === h.id}
                      data-testid={`zapier-remove-hook-${h.id}`}
                      className="w-6 h-6 rounded-md text-gray-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors shrink-0"
                    >
                      {removingId === h.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {hasHooksForEvent && (
              <button
                onClick={sendTest}
                disabled={testing}
                data-testid="zapier-send-test"
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 disabled:text-gray-300 transition-colors"
              >
                {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Send a test “{EVENT_LABELS[hookEvent]}” event
              </button>
            )}
          </div>

          {(status?.eventsSent || 0) > 0 && (
            <p className="text-[11px] text-gray-400" data-testid="zapier-events-sent">
              {status.eventsSent.toLocaleString()} event{status.eventsSent === 1 ? '' : 's'} delivered
              {status.lastEventAt ? ` · last ${new Date(status.lastEventAt).toLocaleString()}` : ''}
            </p>
          )}

          <a
            href="/support?category=integrations"
            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
            data-testid="zapier-help-link"
          >
            <ExternalLink className="w-3 h-3" />
            Zapier setup guide in the Help Center
          </a>

          {/* Rotate-key confirmation modal */}
          <Dialog open={showRotateConfirm} onOpenChange={setShowRotateConfirm}>
            <DialogContent className="max-w-md" data-testid="zapier-rotate-modal">
              <DialogHeader>
                <DialogTitle>Rotate API key?</DialogTitle>
                <DialogDescription>
                  Your current key stops working immediately — any Zap connected with it will
                  break until you paste in the new key. Webhook URLs you added here keep working.
                </DialogDescription>
              </DialogHeader>
              <div className="flex gap-2 justify-end pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowRotateConfirm(false)}
                  data-testid="zapier-rotate-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={doGenerateKey}
                  disabled={keyBusy}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="zapier-rotate-confirm"
                >
                  {keyBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Rotate key
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </Section>
  );
};

const GygIcon = (
  <img src="/images/integrations/gyg-icon.jpg" alt="GetYourGuide" className="w-5 h-5 rounded" />
);

const GmbIcon = (
  <img src="/images/integrations/getmyboat-logo.png" alt="GetMyBoat" className="w-5 h-5 rounded object-contain" />
);

const StatusDot = ({ active, activeText, inactiveText }) => (
  <div className="flex items-center gap-2">
    {active ? (
      <span className="relative flex h-1.5 w-1.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
      </span>
    ) : (
      <span className="inline-flex rounded-full h-1.5 w-1.5 bg-rose-400" />
    )}
    <span className="text-xs text-gray-500">{active ? activeText : inactiveText}</span>
  </div>
);

const CopyRow = ({ label, value, k, testid, copied, onCopy }) => (
  <div>
    <Label className="field-label">{label}</Label>
    <div className="relative mt-1">
      <Input value={value} readOnly className="font-mono pr-9 text-xs text-gray-600 bg-gray-50/60" data-testid={testid} />
      <button
        onClick={() => onCopy(value, k)}
        className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex items-center justify-center transition-colors"
      >
        {copied === k ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  </div>
);

// GetYourGuide + GetMyBoat channel connections — status comes from the same
// providers endpoint the Home manifest dropdown uses.
const ChannelSections = () => {
  const [providers, setProviders] = useState(null);
  const [copied, setCopied] = useState(null);
  const orgSlug = localStorage.getItem('organizationSlug') || '';
  const backendBase = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, '');
  const gygEndpoint = `${backendBase}/api/gyg`;

  useEffect(() => {
    let cancelled = false;
    apiV2.get('/integrations/gmb/status')
      .then((r) => { if (!cancelled) setProviders(r.data?.providers || []); })
      .catch(() => { if (!cancelled) setProviders([]); });
    return () => { cancelled = true; };
  }, []);

  const gyg = providers?.find((p) => p.key === 'getyourguide');
  const gmb = providers?.find((p) => p.key === 'getmyboat');

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      toast.success('Copied');
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <>
      {/* GetYourGuide */}
      <Section
        icon={GygIcon}
        title="GetYourGuide"
        description="Sell your experiences on GetYourGuide — availability, reservations and bookings sync automatically"
      >
        <div className="space-y-4">
          <div data-testid="gyg-status">
            <StatusDot
              active={!!gyg?.active}
              activeText={`Live · ${(gyg?.importedCount || 0).toLocaleString()} GYG booking${gyg?.importedCount === 1 ? '' : 's'} received`}
              inactiveText="Not connected yet — follow the steps below"
            />
          </div>

          <CopyRow label="Your API endpoint (give this to GYG)" value={gygEndpoint} k="gyg-endpoint" testid="gyg-endpoint-input" copied={copied} onCopy={copyText} />
          <CopyRow label="Your Supplier ID" value={orgSlug} k="gyg-supplier" testid="gyg-supplier-input" copied={copied} onCopy={copyText} />

          <div className="rounded-lg bg-gray-50/70 p-3.5 space-y-2">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">How to connect</p>
            {[
              ['1', 'Apply to the GYG Connectivity Program at supplier.getyourguide.com'],
              ['2', 'Ask to connect via their Supplier API'],
              ['3', 'Share your endpoint and Supplier ID with their team'],
              ['4', 'Map your tours using Product IDs: supplierId:serviceId'],
            ].map(([n, txt]) => (
              <div key={n} className="flex items-start gap-2.5">
                <span className="text-xs font-bold text-[#FF5533] mt-px w-3 shrink-0">{n}</span>
                <p className="text-xs text-gray-600 leading-relaxed">{txt}</p>
              </div>
            ))}
          </div>

          <p className="text-[11px] text-gray-400">
            Need your GYG API credentials? Open a{' '}
            <a href="/support#tickets" className="text-blue-600 hover:text-blue-700">Support Center ticket</a>
            {' '}and we&apos;ll set you up.
          </p>
        </div>
      </Section>

      {/* GetMyBoat */}
      <Section
        icon={GmbIcon}
        title="GetMyBoat"
        description="Your GetMyBoat bookings imported automatically — no double-entry, no missed trips"
      >
        <div className="space-y-3">
          <div data-testid="gmb-integration-status">
            <StatusDot
              active={!!gmb?.active}
              activeText={`Linked · ${(gmb?.importedCount || 0).toLocaleString()} bookings imported${gmb?.lastSyncedAt ? ` · last synced ${new Date(gmb.lastSyncedAt).toLocaleString()}` : ''}`}
              inactiveText="Not linked yet"
            />
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">
            {gmb?.active
              ? 'Imports run automatically every few minutes. You can sync on demand from the Bridge — the refresh button shows all connected integrations.'
              : 'This integration is set up by our team on your behalf. Reach out via the '}
            {!gmb?.active && (
              <a href="/support#tickets" className="text-blue-600 hover:text-blue-700">Support Center</a>
            )}
            {!gmb?.active && ' and we\u2019ll finalize it for you — usually same-day.'}
          </p>
        </div>
      </Section>
    </>
  );
};

const ConversionsTab = () => {
  const [showGuide, setShowGuide] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const saveTimer = useRef(null);
  const [settings, setSettings] = useState({
    googleAdsId: '',
    googleAdsLabel: '',
    metaPixelId: '',
    ga4MeasurementId: '',
    gtmContainerId: '',
  });
  // Always-fresh snapshot so a debounced save never sends a stale sibling value
  // when several fields are edited in quick succession.
  const settingsRef = useRef(settings);

  const { data: orgData, isLoading, mutate } = useSWR(
    'organization-settings',
    fetcher,
    { revalidateOnFocus: true, dedupingInterval: 30000 }
  );

  useEffect(() => {
    if (orgData) {
      const next = {
        googleAdsId: orgData.googleAdsId || orgData.google_ads_id || '',
        googleAdsLabel: orgData.googleAdsLabel || orgData.google_ads_label || '',
        metaPixelId: orgData.metaPixelId || orgData.meta_pixel_id || '',
        ga4MeasurementId: orgData.ga4MeasurementId || orgData.google_analytics_id || '',
        gtmContainerId: orgData.gtmContainerId || orgData.gtm_container_id || '',
      };
      setSettings(next);
      settingsRef.current = next;
    }
  }, [orgData]);

  // --- Auto-save (mirrors BrandingTab) -----------------------------------
  const persist = useCallback(async () => {
    const next = settingsRef.current;
    // Auto-prefix AW- on the Google Ads conversion ID if they forgot it.
    let adsId = (next.googleAdsId || '').trim();
    if (adsId && !adsId.startsWith('AW-')) adsId = `AW-${adsId}`;
    // Same treatment for the GTM container ID (always GTM-XXXXXXX, uppercase).
    let gtmId = (next.gtmContainerId || '').trim().toUpperCase();
    if (gtmId && !gtmId.startsWith('GTM-')) gtmId = `GTM-${gtmId}`;

    try {
      await orgApi.updateOrganization({
        googleAdsId: adsId || null,
        googleAdsLabel: next.googleAdsLabel.trim() || null,
        metaPixelId: next.metaPixelId.trim() || null,
        ga4MeasurementId: next.ga4MeasurementId.trim() || null,
        gtmContainerId: gtmId || null,
      });
      // Reflect the normalized ads ID back into the field (cache-only, no refetch
      // so we never reset an input the user might still be editing in another field).
      if (adsId !== next.googleAdsId || gtmId !== next.gtmContainerId) {
        const normalized = { ...next, googleAdsId: adsId, gtmContainerId: gtmId };
        settingsRef.current = normalized;
        setSettings(normalized);
      }
      mutate({ ...(orgData || {}), googleAdsId: adsId, googleAdsLabel: next.googleAdsLabel, metaPixelId: next.metaPixelId, ga4MeasurementId: next.ga4MeasurementId, gtmContainerId: gtmId }, false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1600);
    } catch (error) {
      console.error('Error saving conversion settings:', error);
      toast.error('Failed to save changes');
    }
  }, [orgData, mutate]);

  const scheduleSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(), 700);
  }, [persist]);

  const handleChange = (field, value) => {
    const sanitized = value.replace(/[^a-zA-Z0-9\-_]/g, '');
    const next = { ...settingsRef.current, [field]: sanitized };
    settingsRef.current = next;
    setSettings(next);
    scheduleSave();
  };

  if (showGuide) {
    return <GoogleAdsSetupGuide onBack={() => setShowGuide(false)} />;
  }

  return (
    <TabPageLayout
      title="Integrations"
      subtitle="Connect your ad pixels, analytics and automation tools so bookings flow everywhere they need to"
      loading={isLoading}
      actions={
        <div
          data-testid="conversions-autosave-status"
          className={`hidden sm:inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${
            savedFlash ? 'text-green-600' : 'text-gray-400'
          }`}
        >
          {savedFlash ? <Check className="w-4 h-4" /> : <Cloud className="w-4 h-4" />}
          {savedFlash ? 'Saved' : 'Changes save automatically'}
        </div>
      }
    >
      <div className="max-w-xl space-y-5">
        {/* BookingFlows Beacon */}
        <BfBeaconSection orgId={orgData?.id || localStorage.getItem('organizationId')} />

        {/* QuickBooks Online — LIVE (Intuit production approval granted) */}
        <QuickBooksIntegrationCard orgId={orgData?.id || localStorage.getItem('organizationId')} />

        {/* Google Ads */}
        <Section
          icon={GoogleAdsIcon}
          title="Google Ads"
          description="Attribute bookings back to your Google Ads campaigns"
        >
          <div className="space-y-4">
            <div>
              <Label htmlFor="googleAdsId" className="field-label">Conversion ID</Label>
              <Input
                id="googleAdsId"
                value={settings.googleAdsId}
                onChange={(e) => handleChange('googleAdsId', e.target.value)}
                placeholder="AW-123456789"
                className="font-mono mt-1"
                maxLength={20}
                data-testid="google-ads-id-input"
              />
            </div>
            <div>
              <Label htmlFor="googleAdsLabel" className="field-label">Conversion Label</Label>
              <Input
                id="googleAdsLabel"
                value={settings.googleAdsLabel}
                onChange={(e) => handleChange('googleAdsLabel', e.target.value)}
                placeholder="AbCdEfGhIjKlMnOp"
                className="font-mono mt-1"
                maxLength={30}
                data-testid="google-ads-label-input"
              />
            </div>
          </div>

          <button
            onClick={() => setShowGuide(true)}
            className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium mt-3"
            data-testid="open-google-ads-guide"
          >
            <HelpCircle className="w-4 h-4" />
            How to find your Conversion ID &amp; Label
          </button>
          <a
            href="https://tagassistant.google.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-ads-tag-assistant-link"
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-2 w-fit"
          >
            <ExternalLink className="w-3 h-3" />
            Test your tag with Google Tag Assistant
          </a>
        </Section>

        {/* GA4 */}
        <Section
          icon={GA4Icon}
          title="Google Analytics 4"
          description="Send booking events to your own GA4 property"
        >
          <div>
            <Label htmlFor="ga4MeasurementId" className="field-label">Measurement ID</Label>
            <Input
              id="ga4MeasurementId"
              value={settings.ga4MeasurementId}
              onChange={(e) => handleChange('ga4MeasurementId', e.target.value)}
              placeholder="G-XXXXXXXXXX"
              className="font-mono mt-1"
              maxLength={20}
              data-testid="ga4-measurement-id-input"
            />
          </div>

          <div className="mt-3 space-y-1.5">
            <a
              href="https://support.google.com/analytics/answer/9304153"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 w-fit"
            >
              <ExternalLink className="w-3 h-3" />
              How to find your Measurement ID
            </a>
            <a
              href="https://tagassistant.google.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ga4-tag-assistant-link"
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 w-fit"
            >
              <ExternalLink className="w-3 h-3" />
              Test with Google Tag Assistant
            </a>
            <a
              href="https://analytics.google.com/analytics/web/#/p/debugview"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ga4-debugview-link"
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 w-fit"
            >
              <ExternalLink className="w-3 h-3" />
              Watch live events in GA4 DebugView
            </a>
          </div>
        </Section>

        {/* Google Tag Manager */}
        <Section
          icon={GTMIcon}
          title="Google Tag Manager"
          description="Load your GTM container in the booking widget &amp; lead form — manage all your tags in one place"
        >
          <div>
            <Label htmlFor="gtmContainerId" className="field-label">Container ID</Label>
            <Input
              id="gtmContainerId"
              value={settings.gtmContainerId}
              onChange={(e) => handleChange('gtmContainerId', e.target.value)}
              placeholder="GTM-XXXXXXX"
              className="font-mono mt-1"
              maxLength={15}
              data-testid="gtm-container-id-input"
            />
            <p className="text-xs text-gray-400 mt-1.5">
              Booking events are pushed to the dataLayer automatically — <span className="font-mono">view_item</span>,{' '}
              <span className="font-mono">begin_checkout</span>, <span className="font-mono">purchase</span>,{' '}
              <span className="font-mono">generate_lead</span> — so you can trigger any tag off them.
            </p>
          </div>

          <div className="mt-3 space-y-1.5">
            <a
              href="https://support.google.com/tagmanager/answer/14847097"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="gtm-find-id-link"
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 w-fit"
            >
              <ExternalLink className="w-3 h-3" />
              How to find your Container ID
            </a>
            <a
              href="https://tagassistant.google.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="gtm-tag-assistant-link"
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 w-fit"
            >
              <ExternalLink className="w-3 h-3" />
              Test your container with Google Tag Assistant
            </a>
          </div>
        </Section>

        {/* Meta Pixel */}
        <Section
          icon={MetaIcon}
          title="Meta Pixel"
          description="Track conversions for Facebook &amp; Instagram ads"
        >
          <div>
            <Label htmlFor="metaPixelId" className="field-label">Pixel ID</Label>
            <Input
              id="metaPixelId"
              value={settings.metaPixelId}
              onChange={(e) => handleChange('metaPixelId', e.target.value)}
              placeholder="1234567890123456"
              className="font-mono mt-1"
              maxLength={20}
              data-testid="meta-pixel-id-input"
            />
          </div>

          <div className="mt-3 space-y-1.5">
            <a
              href="https://www.facebook.com/business/help/952192354843755"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 w-fit"
            >
              <ExternalLink className="w-3 h-3" />
              Find your Pixel ID
            </a>
            <a
              href="https://chrome.google.com/webstore/detail/meta-pixel-helper/fdgfkebogiimcoedlicjlajpkdmockpc"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="meta-pixel-helper-link"
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 w-fit"
            >
              <ExternalLink className="w-3 h-3" />
              Test your pixel with Meta Pixel Helper (Chrome extension)
            </a>
          </div>
        </Section>

        {/* Zapier */}
        <ZapierSection />

        {/* Channel connections — GetYourGuide + GetMyBoat */}
        <ChannelSections />
      </div>
    </TabPageLayout>
  );
};

export default ConversionsTab;
